// src/components/Product-zone/FeedZone.jsx
import React, { useEffect, useState } from "react";

// 🧩 Dashboard
import Overview from "./Dashboard/Profile/Overview";
import SmartCardDashboard from "./Dashboard/Profile/SmartCardDashboard";
import KycVerification from "./Dashboard/Profile/KycVerification";
import Preferences from "./Dashboard/Profile/Preferences";
import ConnectedAccounts from "./Dashboard/Profile/ConnectedAccounts";

// 🧩 Charts
import TradingViewChart from "./Market-tools/Charts/TradingViewChart";
import OctaFXMT4WebTerminal from "./Market-tools/Charts/OctaFXMT4WebTerminal";
import OctaFXMT5WebTerminal from "./Market-tools/Charts/OctaFXMT5WebTerminal";
import CTraderWebTerminal from "./Market-tools/Charts/CTraderWebTerminal";
import NinjaTraderWebTerminal from "./Market-tools/Charts/NinjaTraderWebTerminal";
import ThinkorSwimWebLink from "./Market-tools/Charts/ThinkorSwimWebLink";
import CustomChartPlaceholder from "./Market-tools/Charts/CustomChartPlaceholder";

// 🧩 Signals
import TradingViewSignals from "./Market-tools/Signals/TradingViewSignals";
import TradingSignalBox from "./Market-tools/Signals/TradingSignalBox";

// 🧩 Directory
import BrokersList from "./Directory/BrokersList";
import TradersList from "./Directory/TradersList";

// 🧩 Academics
import TradingFundamentalsCourse from "./Academics/Courses/TradingFundamentalsCourse";

// 🧩 feedzone
import TraderCommunityChat from "./community/groups/tradinggroups/TraderCommunityChat";

export default function FeedZone({
  activeZone = "office",
  activeSub = null,
  splitMode = 1,
  fitScreen = true,
  orientation = "vertical",
  paneContent = [],
  setPaneContent,
  setActivePane,
  activePane = 0,
}) {
  // ─── Layout ─────────────────────────────
  const gridClass =
    splitMode === 1
      ? "grid-cols-1"
      : splitMode === 2
        ? orientation === "horizontal"
          ? "grid-rows-2"
          : "grid-cols-2"
        : splitMode === 4
          ? "grid-cols-2 grid-rows-2"
          : "grid-cols-1";

  const [fullscreenPane, setFullscreenPane] = useState(null);
  useEffect(() => {}, [splitMode, fitScreen]);

  // ─── Helpers ─────────────────────────────
  function normalizeLabel(label = "") {
    return label.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  const COMPONENT_MAP = {
    overview: <Overview />,
    smartcard: <SmartCardDashboard />,
    kycverification: <KycVerification />,
    preferences: <Preferences />,
    connectedaccounts: <ConnectedAccounts />,
  };

  // ─── Update active pane ─────────────────────────────
  useEffect(() => {
    if (!activeSub || typeof setPaneContent !== "function") return;
    setPaneContent((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next[activePane] = activeSub;
      return next;
    });
  }, [activeSub, activePane, setPaneContent]);

  // ─── Chart Renderer ─────────────────────────────
  const renderChart = (platform) => {
    switch (platform) {
      case "TradingView":
        return <TradingViewChart symbol="XAUUSD" />;
      case "MetaTrader 4 (MT4)":
        return <OctaFXMT4WebTerminal />;
      case "MetaTrader 5 (MT5)":
        return <OctaFXMT5WebTerminal />;
      case "cTrader":
        return <CTraderWebTerminal />;
      case "NinjaTrader":
        return <NinjaTraderWebTerminal />;
      case "ThinkorSwim":
        return <ThinkorSwimWebLink />;
      case "Custom Chart":
        return <CustomChartPlaceholder />;
      default:
        return (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            No chart available for {platform || "this platform"}.
          </div>
        );
    }
  };

  // ─── Pane Renderer ─────────────────────────────
  const RenderPane = ({ paneIdx }) => {
    const content = paneContent[paneIdx];
    const selectedLabel =
      content?.child?.label || content?.label || content?.parent?.label || "";

    const normalizedKey = normalizeLabel(selectedLabel);
    const Component = COMPONENT_MAP[normalizedKey];

    // 🟢 1️⃣ Office
    if (activeZone === "office") {
      return (
        <div className="flex-1 overflow-auto bg-slate-950/30 p-3 text-white">
          {Component || (
            <div className="flex h-full items-center justify-center text-sm text-slate-500">
              No data available for this section
            </div>
          )}
        </div>
      );
    }

    // 🟢 2️⃣ Market Tools → Charts
    if (activeZone === "tools" && content?.parent?.label === "Charts") {
      const platform = content?.child?.label || content?.label || "TradingView";
      return (
        <div className="flex-1 overflow-hidden bg-slate-950 text-white">
          {renderChart(platform)}
        </div>
      );
    }

    // 🟢 3️⃣ Market Tools → Signals (handles nested Free Signals)
    if (activeZone === "tools") {
      const parentLabel = content?.parent?.label || "";
      const childLabel = content?.child?.label || "";
      const grandParentLabel = content?.parent?.parent?.label || "";

      // ✅ Normal signals
      if (
        parentLabel === "Signals" &&
        ["Copy Signals", "Community Signals", "Premium Alerts"].includes(
          childLabel,
        )
      ) {
        return <TradingViewSignals type={childLabel.toLowerCase()} />;
      }

      // ✅ Free Signals → TradingView Signals
      if (
        parentLabel === "Free Signals" ||
        grandParentLabel === "Free Signals" ||
        childLabel === "TradingView Signals"
      ) {
        const [symbol, setSymbol] = useState("OANDA:XAUUSD");

        return (
          <div className="flex h-full flex-col overflow-auto bg-slate-950 p-4 text-white">
            <h2 className="mb-3 text-lg font-semibold">
              📊 TradingView Signals — {symbol}
            </h2>

            <div className="mb-3 flex items-center gap-2">
              <label className="text-xs text-slate-400">Select Symbol:</label>
              <select
                className="rounded-md border border-slate-700 bg-slate-800 p-1 text-sm text-white"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
              >
                <option value="OANDA:XAUUSD">XAUUSD (Gold)</option>
                <option value="OANDA:EURUSD">EURUSD (Euro/USD)</option>
                <option value="OANDA:GBPUSD">GBPUSD (Pound/USD)</option>
                <option value="OANDA:USDJPY">USDJPY (Dollar/Yen)</option>
                <option value="BINANCE:BTCUSDT">BTCUSDT (Crypto)</option>
                <option value="BINANCE:ETHUSDT">ETHUSDT (Crypto)</option>
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-lg bg-black p-2 shadow">
                <TradingViewChart symbol={symbol} />
              </div>
              <div className="rounded-lg bg-black p-2 shadow">
                <TradingSignalBox symbol={symbol} />
              </div>
            </div>
          </div>
        );
      }

      // 🟣 Fallback for Signals root
      if (parentLabel === "Signals") {
        return (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Select a signal category to continue.
          </div>
        );
      }
    }

    // 🟢 4️⃣ Directory → Brokers
    if (activeZone === "directory" && selectedLabel === "Brokers") {
      return <BrokersList />;
    }

    // 🟢 Directory → Traders
    if (activeZone === "directory" && selectedLabel === "Traders") {
      return <TradersList />;
    }

    // 🟢 5️⃣ Academics → Courses
    if (activeZone === "academics") {
      const selectedLabel = content?.child?.label || content?.label || "";

      if (
        selectedLabel === "Trading Basics" ||
        selectedLabel === "Trading Fundamentals" ||
        selectedLabel === "Trading Fundamentals Course"
      ) {
        return <TradingFundamentalsCourse />;
      }

      return (
        <div className="flex h-full items-center justify-center text-sm text-slate-500">
          Select a course to begin learning.
        </div>
      );
    }

    // 🟢 6 Community → Groups → Trading Groups
    if (activeZone === "community") {
      const parentLabel = content?.parent?.label || "";
      const childLabel = content?.child?.label || "";
      const selectedLabel = content?.label || "";

      if (
        parentLabel === "Groups" &&
        (childLabel === "Trading Groups" || selectedLabel === "Trading Groups")
      ) {
        return <TraderCommunityChat />;
      }
    }

    // 🟣 Default fallback
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
        {selectedLabel} zone Under development
      </div>
    );
  };

  // ─── Fullscreen toggle ─────────────────────────────
  const handleDoubleClick = (idx) => {
    setFullscreenPane((prev) => (prev === idx ? null : idx));
  };

  // ─── Render Layout ─────────────────────────────
  return (
    <section className="flex h-full flex-1 flex-col bg-slate-950/40">
      {/* Main grid */}
      <div
        className={`grid min-h-0 flex-1 transition-all duration-300 ${
          fullscreenPane === null ? gridClass : "grid-cols-1"
        }`}
      >
        {Array.from({ length: splitMode }).map((_, i) => {
          if (fullscreenPane !== null && fullscreenPane !== i) return null;
          return (
            <div
              key={i}
              onClick={() => setActivePane(i)}
              onDoubleClick={() => handleDoubleClick(i)}
              className={`flex min-h-0 cursor-pointer flex-col border border-slate-800 bg-slate-900/70 transition-all duration-300 ${
                activePane === i ? "ring-1 ring-blue-500" : ""
              } ${fullscreenPane === i ? "z-50 col-span-full row-span-full" : ""}`}
            >
              <RenderPane paneIdx={i} />
            </div>
          );
        })}
      </div>

      {/* Status bar */}
      <div className="flex h-8 items-center justify-between border-t border-slate-800 bg-slate-900/80 px-4 text-[12px] text-slate-400">
        <span>
          🧭 Workspace:{" "}
          <span className="font-medium text-blue-400">
            {activePane + 1} / {splitMode}
          </span>
        </span>
        <span>
          Zone: <span className="font-medium text-green-400">{activeZone}</span>
        </span>
        <span>
          Submenu:{" "}
          <span className="font-medium text-purple-400">
            {paneContent[activePane]?.child?.label ||
              paneContent[activePane]?.label ||
              "TradingView"}
          </span>
        </span>
      </div>
    </section>
  );
}
